// Utility for Tool Usage / Entitlements API.
// Every call accepts an optional Bearer token, so auth can be passed in at call site.

export type ToolUsageDto = {
    toolCode: string;
    subscribedQty: number;
    usedQty: number;
    unitName?: string | null;
};

// Backend returns a map keyed by toolCode:
// { "OPTIMIZATION_INGEST_SCHEDULER": { toolCode, subscribedQty, usedQty, unitName }, ... }
export type ToolsUsageMap = Record<string, ToolUsageDto>;

const BASE = String(process.env.REACT_APP_SPRING_BOOT_API_URL || "").replace(/\/+$/, "");

// -----------------------------------------------------------------------------
// API surface
// -----------------------------------------------------------------------------

/**
 * GET /api/tools/usage
 * Returns quantities for all tools for the current user.
 * Intended for marketplace + left nav in a single call.
 */
export async function getAllToolsUsage(token?: string | null): Promise<ToolsUsageMap> {
    const res = await fetch(`${BASE}/api/tools/usage`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET /api/tools/usage failed: ${res.status}`);
    return res.json();
}

/**
 * GET /api/tools/{toolCode}/usage
 * Returns quantities for a single tool for the current user.
 * Intended for tool pages (Optimizer header refresh, etc.).
 */
export async function getToolUsage(toolCode: string, token?: string | null): Promise<ToolUsageDto> {
    if (!toolCode) throw new Error("toolCode is required");

    const res = await fetch(`${BASE}/api/tools/${encodeURIComponent(toolCode)}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`GET /api/tools/${toolCode}/usage failed: ${res.status}`);
    return res.json();
}

export const toolUsageApi = {
    getAllToolsUsage,
    getToolUsage,
};
